/**
 * Köçürmə ekranı — köhnə provayderdən məktubların gətirilməsi.
 *
 * AXIN ÜÇ ADDIMDIR
 *   1. Köhnə ünvan yazılır → provayder MX-ə görə avtomatik tanınır
 *   2. Provayderin tələb etdiyi açar növü izah edilir, bağlantı sınanır
 *   3. Köçürmə başlayır, irəliləyiş göstərilir
 *
 * ⚠️ NİYƏ ƏVVƏLCƏ SINAQ, SONRA KÖÇÜRMƏ
 * Bu ekranın ən çox rast gələcəyi problem yanlış açardır — Gmail, iCloud,
 * Yahoo və Yandex adi şifrəni ümumiyyətlə qəbul etmir. Sınaq addımı
 * olmasaydı, istifadəçi «köçürmə başladı» görər, sonra uğursuzluq alar və
 * səbəbini bizim sistemdə axtarardı. İndi səhv dərhal və aydın deyilir.
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import type { MigrationProvider, MigrationJob } from '../types';

interface Props {
  onClose: () => void;
}

/** İşin bitib-bitmədiyi — sorğulamanı dayandırmaq üçün. */
function isFinal(status: string): boolean {
  return ['done', 'failed', 'cancelled', 'paused'].includes(status);
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'gözləyir',
  running: 'köçürülür',
  paused: 'dayandırılıb',
  done: 'tamamlandı',
  failed: 'uğursuz',
  cancelled: 'ləğv edildi',
};

export function Migration({ onClose }: Props): React.ReactElement {
  const [providers, setProviders] = useState<MigrationProvider[]>([]);
  const [jobs, setJobs] = useState<MigrationJob[]>([]);

  const [email, setEmail] = useState('');
  const [providerId, setProviderId] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(993);
  const [password, setPassword] = useState('');

  const [detecting, setDetecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState<{ folders: number; messages: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * Davam etdirmə parolu — hansı iş üçün açıq olduğu və yazılan dəyər.
   *
   * ⚠️ AYRICA SAHƏ MƏCBURİDİR. Parolu heç yerdə saxlamırıq, ona görə
   * `resume` ucu onu hər dəfə tələb edir. Yalnız düymə qoysaydıq
   * (əvvəlki cəhdim belə idi), hər klik `password_required` xətası
   * qaytarardı — yəni düymə görünür, amma dalanadır.
   */
  const [resumeFor, setResumeFor] = useState<string | null>(null);
  const [resumePw, setResumePw] = useState('');

  const provider = providers.find((p) => p.id === providerId) ?? null;
  const active = jobs.find((j) => !isFinal(j.status)) ?? null;

  useEffect(() => {
    void api
      .migrationProviders()
      .then((r) => {
        setProviders(r.providers);

        // ⚠️ ONBOARDING-DƏ TAPILAN PROVAYDER ÖNCƏDƏN SEÇİLİR.
        // Domen əlavə ediləndə MX-ə baxıb köhnə xidməti onsuz da tapmışdıq
        // (dns/provider.ts). Müştəridən eyni sualı ikinci dəfə soruşmaq
        // lazımsız addımdır və hər lazımsız addım tərketmədir.
        // İpucu BİR DƏFƏ işlədilir və dərhal silinir: müştəri sonradan
        // başqa xidmətdən köçürmək istəsə, köhnə seçim ona mane olmasın.
        try {
          const hint = localStorage.getItem('mailora.migration.hint');
          if (hint && r.providers.some((p) => p.id === hint)) {
            setProviderId(hint);
          }
          if (hint) localStorage.removeItem('mailora.migration.hint');
        } catch {
          // Şəxsi rejim — sadəcə öncədən seçim olmayacaq.
        }
      })
      .catch(() => undefined);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.migrationJobs();
      setJobs(r.jobs);
    } catch {
      // Sorğulama uğursuzluğu ekranı sındırmamalıdır.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ⚠️ Sorğulama YALNIZ iş gedirkən. Sabit interval qoysaq, ekran açıq
  // qalan hər brauzer boş yerə hər 3 saniyədə serverə müraciət edərdi.
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => void refresh(), 3000);
    return () => clearInterval(t);
  }, [active, refresh]);

  /** Ünvan yazılıb bitəndə provayderi təxmin edirik. */
  async function detect(): Promise<void> {
    if (!email.includes('@')) return;
    setDetecting(true);
    setError(null);
    try {
      const r = await api.migrationDetect(email);
      if (r.alreadyOurs) {
        setError('Bu ünvan artıq Mailora-dadır — köçürməyə ehtiyac yoxdur.');
        return;
      }
      if (r.provider) {
        setProviderId(r.provider);
        const p = providers.find((x) => x.id === r.provider);
        if (p) {
          setHost(p.imapHost);
          setPort(p.imapPort || 993);
        }
        setNotice(null);
      } else {
        setProviderId('other');
        setNotice('Provayder avtomatik tanınmadı — IMAP server adını əl ilə yazın.');
      }
    } catch {
      setProviderId('other');
    } finally {
      setDetecting(false);
    }
  }

  function chooseProvider(id: string): void {
    setProviderId(id);
    setTested(null);
    const p = providers.find((x) => x.id === id);
    if (p?.imapHost) {
      setHost(p.imapHost);
      setPort(p.imapPort || 993);
    }
  }

  async function test(e: FormEvent): Promise<void> {
    e.preventDefault();
    setTesting(true);
    setError(null);
    setTested(null);
    try {
      const r = await api.migrationTest(host, port, email, password);
      setTested({ folders: r.folders, messages: r.messages });
    } catch (err) {
      // Serverin `reason` sahəsi insan cümləsidir — `code` deyil, onu göstəririk.
      const reason = err instanceof ApiError ? err.body['reason'] : null;
      setError(typeof reason === 'string' ? reason : 'Bağlantı alınmadı.');
    } finally {
      setTesting(false);
    }
  }

  /**
   * Yarımçıq işi davam etdirir.
   *
   * ⚠️ ŞİFRƏ YENİDƏN LAZIMDIR. Onu heç yerdə saxlamırıq (bax formadakı
   * izah), ona görə davam etdirmə yalnız istifadəçi şifrəni yenidən
   * yazandan sonra mümkündür. Arxa tərəf sessiyada saxlanılan iş
   * parametrlərini işlədir, yalnız açar çatmır.
   */
  async function resume(id: string): Promise<void> {
    if (!resumePw) return;
    setBusy(true);
    setError(null);
    try {
      await api.migrationResume(id, resumePw);
      setResumePw('');
      setResumeFor(null);
      setNotice('Köçürmə davam etdirilir. Artıq köçən məktublar təkrarlanmayacaq.');
      await refresh();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setError(
        code === 'not_resumable'
          ? 'Bu iş artıq davam etdirilə bilmir — yeni köçürmə başladın.'
          : code === 'server_busy' || code === 'already_running'
            ? 'Hazırda başqa köçürmə gedir. Bir azdan yenidən cəhd edin.'
            : 'Davam etdirilə bilmədi. Şifrənin düzgün olduğunu yoxlayın.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function start(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.migrationStart(providerId, host, port, email, password);
      setPassword('');
      setTested(null);
      setNotice('Köçürmə başladı. Bu pəncərəni bağlaya bilərsiniz — iş arxa planda davam edir.');
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'already_running'
          ? 'Bu qutu üçün artıq bir köçürmə gedir.'
          : 'Köçürmə başladıla bilmədi.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id: string): Promise<void> {
    await api.migrationCancel(id).catch(() => undefined);
    await refresh();
  }

  const unsupported = provider && (provider.credential === 'no_imap' || provider.credential === 'oauth_only');

  return (
    <div className="admin">
      <h1>Köhnə poçtu köçür</h1>

      <p className="mig-lead">
        Köhnə provayderinizdəki məktubları qovluq quruluşu ilə birlikdə bura gətiririk.
        Köhnə hesabınıza toxunulmur — məktublar orada da qalır.
      </p>

      {/*
        ⚠️ BİLDİRİŞ VƏ XƏTA `!active` QAPISINDAN KƏNARDADIR.
        Əvvəl hər ikisi formanın içində idi. `start()` bildirişi qoyur,
        sonra `refresh()` işi yükləyir, `active` dolur və BÜTÜN forma
        (bildirişlə birlikdə) gizlənir — yəni «Köçürmə başladı» cümləsi
        HEÇ VAXT görünmürdü. Eyni səbəbdən `detect()`-in «Bu ünvan artıq
        Mailora-dadır» xətası da ölü idi.
      */}
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}
      {notice && !error && (
        <p className="login-ok" role="status">
          {notice}
        </p>
      )}

      {jobs.length > 0 && (
        <section className="admin-card">
          <h2>Köçürmələr</h2>
          {jobs.map((j) => {
            const done = j.copied + j.skipped + j.failed;
            const pct = j.total > 0 ? Math.min(100, Math.round((done / j.total) * 100)) : 0;
            return (
              <div key={j.id} className={`mig-job is-${j.status}`}>
                <div className="mig-job-head">
                  <strong>{j.sourceUser}</strong>
                  <span className={`mig-badge is-${j.status}`}>
                    {STATUS_LABEL[j.status] ?? j.status}
                  </span>
                  {/*
                    ⚠️ «DAVAM ET» YALNIZ YARIMÇIQ QALANLARDA.
                    `done` işdə mənasızdır, `cancelled` isə istifadəçinin
                    şüurlu qərarıdır — onu «davam etdir» deyə təklif etmək
                    qərarını sual altına qoymaqdır. Yalnız `failed` və
                    `paused` — yəni İSTİFADƏÇİNİN İSTƏMƏDİYİ dayanmalar.
                  */}
                  {(j.status === 'failed' || j.status === 'paused') && (
                    <button
                      className="btn-text is-primary"
                      disabled={busy}
                      onClick={() => {
                        setResumeFor(resumeFor === j.id ? null : j.id);
                        setResumePw('');
                      }}
                    >
                      Davam et
                    </button>
                  )}
                  {!isFinal(j.status) && (
                    <button className="btn-text btn-danger" onClick={() => void cancel(j.id)}>
                      Dayandır
                    </button>
                  )}
                </div>

                {j.total > 0 && (
                  <div className="mig-bar" role="progressbar" aria-valuenow={pct}>
                    <div className="mig-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                )}

                <div className="mig-stats">
                  <span>
                    <b>{j.copied}</b> köçürüldü
                  </span>
                  {j.skipped > 0 && (
                    <span>
                      <b>{j.skipped}</b> artıq var idi
                    </span>
                  )}
                  {j.failed > 0 && (
                    <span className="is-bad">
                      <b>{j.failed}</b> alınmadı
                    </span>
                  )}
                  {j.total > 0 && <span>cəmi {j.total}</span>}
                  {j.currentFolder && !isFinal(j.status) && <span>· {j.currentFolder}</span>}
                </div>

                {j.message && <p className="mig-msg">{j.message}</p>}
                {j.hint && <p className="mig-hint">{j.hint}</p>}

                {/*
                  DAVAM ETDİRMƏ FORMASI.
                  ⚠️ SİSTEMİN ÖZ GÖSTƏRİŞİ İCRA EDİLƏ BİLMƏLİDİR.
                  API yenidən başlayanda `recoverStaleJobs` işi `paused`
                  edir və ipucuna «Şifrəni yenidən daxil edib davam edin»
                  yazır. Əvvəl bu cümlə ekranda görünürdü, amma nə sahə,
                  nə düymə vardı — istifadəçidən icra edə bilmədiyi əməl
                  tələb olunurdu. İndi ipucunun dediyi yer elə buradadır.
                */}
                {resumeFor === j.id && (
                  <form
                    className="mig-resume"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void resume(j.id);
                    }}
                  >
                    <label className="field">
                      <span>{j.sourceUser} hesabının şifrəsi</span>
                      <input
                        type="password"
                        autoComplete="off"
                        autoFocus
                        value={resumePw}
                        onChange={(e) => setResumePw(e.target.value)}
                      />
                      <small className="field-hint">
                        Şifrəni saxlamırıq — ona görə hər davam etdirmədə yenidən lazımdır.
                      </small>
                    </label>
                    <button type="submit" className="btn-primary" disabled={busy || !resumePw}>
                      {busy ? 'Davam edilir…' : 'Davam et'}
                    </button>
                  </form>
                )}
              </div>
            );
          })}
        </section>
      )}

      {!active && (
        <form className="admin-card mig-form" onSubmit={test}>
          <h2>Yeni köçürmə</h2>

          <label className="field">
            <span>Köhnə e-poçt ünvanınız</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setTested(null);
              }}
              onBlur={() => void detect()}
              placeholder="ad@gmail.com"
            />
          </label>
          {detecting && <p className="login-hint">Provayder müəyyən edilir…</p>}

          <label className="field">
            <span>Provayder</span>
            <select value={providerId} onChange={(e) => chooseProvider(e.target.value)}>
              <option value="">— seçin —</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          {provider && provider.setupSteps.length > 0 && (
            <div className={`mig-steps${unsupported ? ' is-blocked' : ''}`}>
              <strong>
                {unsupported
                  ? 'Bu provayderdən avtomatik köçürmə mümkün deyil'
                  : 'Əvvəlcə bunları edin'}
              </strong>
              <ol>
                {provider.setupSteps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
              {provider.setupUrl && (
                <a className="linkish" href={provider.setupUrl} target="_blank" rel="noreferrer">
                  Açarı yaratmaq üçün səhifəni aç →
                </a>
              )}
            </div>
          )}

          {provider?.warning && <p className="mig-warn">{provider.warning}</p>}

          {!unsupported && providerId && (
            <>
              <div className="mig-row">
                <label className="field">
                  <span>IMAP server</span>
                  <input
                    required
                    value={host}
                    onChange={(e) => {
                      setHost(e.target.value);
                      setTested(null);
                    }}
                    placeholder="imap.provayder.com"
                  />
                </label>
                <label className="field mig-port">
                  <span>Port</span>
                  <input
                    type="number"
                    value={port}
                    onChange={(e) => setPort(Number(e.target.value))}
                  />
                </label>
              </div>

              <label className="field">
                <span>
                  {provider?.credential === 'app_password' ? 'Tətbiq şifrəsi' : 'Şifrə'}
                </span>
                <input
                  type="password"
                  required
                  autoComplete="off"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setTested(null);
                  }}
                />
              </label>
              {/*
                ⚠️ Bu cümlə qəsdən buradadır. İstifadəçidən BAŞQA şirkətdəki
                hesabının açarını istəyirik — nə üçün və nə qədər saxladığımızı
                deməsək, haqlı olaraq etibar etməz.
              */}
              <p className="login-hint">
                Bu açar yalnız köçürmə müddətində işlədilir və heç yerdə saxlanılmır.
              </p>


              {tested ? (
                <>
                  <p className="login-ok" role="status">
                    Bağlantı uğurludur: {tested.folders} qovluq, {tested.messages} məktub tapıldı.
                  </p>
                  <button type="button" className="btn-primary" disabled={busy} onClick={() => void start()}>
                    {busy ? 'Başladılır…' : `${tested.messages} məktubu köçür`}
                  </button>
                </>
              ) : (
                <button type="submit" className="btn-primary" disabled={testing}>
                  {testing ? 'Yoxlanılır…' : 'Bağlantını yoxla'}
                </button>
              )}
            </>
          )}
        </form>
      )}

      <p className="mig-back">
        <button type="button" className="btn-text" onClick={onClose}>
          ← Poçta qayıt
        </button>
      </p>
    </div>
  );
}
